import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DashboardStats {
  publishedPages: number;
  draftPages: number;
  reviewPages: number;
  archivedPages: number;
  seoIssues: number;
  redirects: number;
}

export interface RecentUpdate {
  id: string;
  title: string;
  type: string;
  updatedAt: string;
  status: string;
  slug: string;
}

export interface SEOIssue {
  id: string;
  pageId: string;
  pageTitle: string;
  pageSlug: string;
  issue: string;
  severity: 'warning' | 'info' | 'error';
}

/**
 * Dashboard stats aggregated from both page_content and content_items.
 * NOTE: page_content does NOT have a `seo` column — SEO issues come only from content_items.
 */
export const useDashboardStats = () => {
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async (): Promise<DashboardStats> => {
      // Fetch both tables in parallel
      const [pagesRes, itemsRes, redirectsRes] = await Promise.all([
        supabase.from('page_content').select('id, status'),
        supabase.from('content_items').select('id, status, seo'),
        supabase.from('redirects').select('id', { count: 'exact', head: true }).eq('is_active', true),
      ]);

      if (pagesRes.error) throw pagesRes.error;
      if (itemsRes.error) throw itemsRes.error;
      if (redirectsRes.error) throw redirectsRes.error;

      const pages = pagesRes.data || [];
      const items = (itemsRes.data || []) as Array<{ id: string; status: string; seo: Record<string, any> }>;

      // Combine statuses from both tables
      const allStatuses = [...pages.map(p => p.status), ...items.map(i => i.status)];

      const publishedPages = allStatuses.filter(s => s === 'published').length;
      const draftPages = allStatuses.filter(s => s === 'draft').length;
      const reviewPages = allStatuses.filter(s => s === 'review').length;
      const archivedPages = allStatuses.filter(s => s === 'archived').length;

      // SEO issues only from content_items (page_content has no seo column)
      let seoIssues = 0;
      items.filter(i => i.status !== 'archived').forEach(item => {
        const seo = item.seo || {};
        if (!seo.title || String(seo.title).trim() === '') seoIssues++;
        else if (String(seo.title).length > 60) seoIssues++;
        if (!seo.description || String(seo.description).trim() === '') seoIssues++;
      });

      return {
        publishedPages,
        draftPages,
        reviewPages,
        archivedPages,
        seoIssues,
        redirects: redirectsRes.count || 0,
      };
    },
  });
};

/**
 * Recent updates from both page_content and content_items, merged by updated_at.
 */
export const useRecentUpdates = (limit: number = 5) => {
  return useQuery({
    queryKey: ['recent-updates', limit],
    queryFn: async (): Promise<RecentUpdate[]> => {
      const [pagesRes, itemsRes] = await Promise.all([
        supabase
          .from('page_content')
          .select('id, page_title, status, updated_at, slug')
          .order('updated_at', { ascending: false })
          .limit(limit),
        supabase
          .from('content_items')
          .select('id, title, type, status, updated_at, slug')
          .order('updated_at', { ascending: false })
          .limit(limit),
      ]);

      if (pagesRes.error) throw pagesRes.error;
      if (itemsRes.error) throw itemsRes.error;

      const pageUpdates: RecentUpdate[] = (pagesRes.data || []).map((p: any) => ({
        id: p.id,
        title: p.page_title,
        type: 'page',
        updatedAt: formatRelativeTime(p.updated_at),
        status: p.status,
        slug: p.slug,
      }));

      const itemUpdates: RecentUpdate[] = (itemsRes.data || []).map((i: any) => ({
        id: i.id,
        title: i.title,
        type: i.type || 'content',
        updatedAt: formatRelativeTime(i.updated_at),
        status: i.status,
        slug: i.slug,
      }));

      // Merge and sort by recency, take top N
      // We need raw dates for sorting, so re-fetch
      const allRaw = [
        ...(pagesRes.data || []).map((p: any) => ({ ...p, _title: p.page_title, _type: 'page' })),
        ...(itemsRes.data || []).map((i: any) => ({ ...i, _title: i.title, _type: i.type || 'content' })),
      ];
      allRaw.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

      return allRaw.slice(0, limit).map((row: any) => ({
        id: row.id,
        title: row._title,
        type: row._type,
        updatedAt: formatRelativeTime(row.updated_at),
        status: row.status,
        slug: row.slug,
      }));
    },
  });
};

/**
 * SEO issues computed from content_items.seo JSONB only.
 * page_content does not have a seo column.
 */
export const useSEOIssues = () => {
  return useQuery({
    queryKey: ['seo-issues'],
    queryFn: async (): Promise<SEOIssue[]> => {
      const { data, error } = await supabase
        .from('content_items')
        .select('id, title, slug, seo, status')
        .neq('status', 'archived');

      if (error) throw error;

      const issues: SEOIssue[] = [];

      (data || []).forEach((item: any) => {
        const seo = item.seo || {};

        if (!seo.title || String(seo.title).trim() === '') {
          issues.push({
            id: `${item.id}-no-title`,
            pageId: item.id,
            pageTitle: item.title,
            pageSlug: item.slug,
            issue: 'Missing SEO title',
            severity: 'error',
          });
        } else if (String(seo.title).length > 60) {
          issues.push({
            id: `${item.id}-title-long`,
            pageId: item.id,
            pageTitle: item.title,
            pageSlug: item.slug,
            issue: `SEO title too long (${String(seo.title).length} chars)`,
            severity: 'warning',
          });
        }

        if (!seo.description || String(seo.description).trim() === '') {
          issues.push({
            id: `${item.id}-no-desc`,
            pageId: item.id,
            pageTitle: item.title,
            pageSlug: item.slug,
            issue: 'Missing meta description',
            severity: 'error',
          });
        } else if (String(seo.description).length > 160) {
          issues.push({
            id: `${item.id}-desc-long`,
            pageId: item.id,
            pageTitle: item.title,
            pageSlug: item.slug,
            issue: `Meta description too long (${String(seo.description).length} chars)`,
            severity: 'warning',
          });
        }

        if (!seo.canonicalUrl) {
          issues.push({
            id: `${item.id}-no-canonical`,
            pageId: item.id,
            pageTitle: item.title,
            pageSlug: item.slug,
            issue: 'No canonical URL set',
            severity: 'info',
          });
        }
      });

      return issues;
    },
  });
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  if (diffHours > 0) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  if (diffMins > 0) return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
  return 'Just now';
}
